import { useCallback, useEffect, useState } from "react";
import { addDays, subDays } from "date-fns";

export type ViewMode = "day" | "week" | "fortnight" | "month";

const LS_VIEW_MODE = "agenda_viewMode";

/**
 * Hook que gerencia data âncora + viewMode + navegação + atalhos de teclado.
 * Persiste viewMode em localStorage.
 *
 * `forceMode` permite override (ex: mobile força "day") — mantém a preferência
 * do user persistida sem sobrescrever.
 */
export function useAgendaNavigation({ forceMode }: { forceMode?: ViewMode } = {}) {
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [storedViewMode, setStoredViewMode] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "week";
    const stored = localStorage.getItem(LS_VIEW_MODE);
    if (stored === "day" || stored === "fortnight" || stored === "month") return stored;
    return "week";
  });

  const viewMode: ViewMode = forceMode ?? storedViewMode;

  const changeViewMode = useCallback((m: ViewMode) => {
    setStoredViewMode(m);
    if (typeof window !== "undefined") localStorage.setItem(LS_VIEW_MODE, m);
  }, []);

  const goToday = useCallback(() => setAnchorDate(new Date()), []);

  const navigate = useCallback(
    (direction: 1 | -1) => {
      setAnchorDate((d) => {
        if (viewMode === "day") return direction === 1 ? addDays(d, 1) : subDays(d, 1);
        if (viewMode === "week") return direction === 1 ? addDays(d, 7) : subDays(d, 7);
        if (viewMode === "fortnight")
          return direction === 1 ? addDays(d, 14) : subDays(d, 14);
        const next = new Date(d);
        next.setMonth(next.getMonth() + direction);
        return next;
      });
    },
    [viewMode],
  );

  // Atalhos: ← / → navega, T volta pra hoje
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement) {
        const tag = e.target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigate(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigate(1);
      } else if (e.key === "t" || e.key === "T") {
        e.preventDefault();
        setAnchorDate(new Date());
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  return {
    anchorDate,
    setAnchorDate,
    viewMode,
    /** Preferência persistida do usuário — independente do forceMode. */
    storedViewMode,
    changeViewMode,
    navigate,
    goToday,
  };
}
